<app-navbar />

<main id="main-content">
  <section class="page-hero">
    <div class="container">
      <app-breadcrumb [trail]="[{ label: 'Terms of Service' }]" />
      <span class="eyebrow">Legal</span>
      <h1>Terms of Service</h1>
      <p>Last updated {{ updated }}</p>
    </div>
  </section>

  <section class="section">
    <div class="container">
      <div class="legal-layout">
        <aside class="legal-toc">
          <p class="legal-toc-title">On this page</p>
          <nav>
            <a [routerLink]="[]" fragment="use">Use of the service</a>
            <a [routerLink]="[]" fragment="data">Verification data</a>
            <a [routerLink]="[]" fragment="accuracy">Accuracy and disclaimers</a>
            <a [routerLink]="[]" fragment="pricing">Pricing and billing</a>
            <a [routerLink]="[]" fragment="refunds">Refunds and billing disputes</a>
            <a [routerLink]="[]" fragment="cancellation">Cancellation and account closure</a>
            <a [routerLink]="[]" fragment="support">Support and availability</a>
            <a [routerLink]="[]" fragment="ip">Intellectual property</a>
            <a [routerLink]="[]" fragment="liability">Limitation of liability</a>
            <a [routerLink]="[]" fragment="disputes">Dispute resolution and governing law</a>
            <a [routerLink]="[]" fragment="changes">Changes to these terms</a>
            <a [routerLink]="[]" fragment="contact">Contact us</a>
          </nav>
        </aside>

        <div class="legal-body">
          <div class="prose legal-prose">
            <p class="legal-intro">
              These Terms of Service govern your use of the website and services provided by
              {{ site.legalName }}, operating as {{ site.name }}. By creating an account or using the
              service, you agree to these terms.
            </p>

            <h2 id="use">Use of the service</h2>
            <p>
              You may use the service only for lawful, legitimate verification purposes and in
              compliance with all laws and regulations that apply to your business. You are
              responsible for the activity that occurs under your account and for keeping your
              credentials secure.
            </p>

            <h2 id="data">Verification data</h2>
            <p>
              You are responsible for the identity records you submit and for having a lawful basis to
              submit them. You agree not to use verification results to unlawfully discriminate against
              or harm any individual, and to use results in line with the rules that apply to your
              industry.
            </p>

            <h2 id="accuracy">Accuracy and disclaimers</h2>
            <p>
              We work hard to provide fresh and accurate death data, and every match includes a
              confidence score. However, the service is provided on an as is basis. We do not guarantee
              that results are complete or error free, and you should apply appropriate review before
              taking action on a result.
            </p>

            <h2 id="pricing">Pricing and billing</h2>
            <p>
              Usage is billed according to our published pricing. Standard verification is charged per
              record, with bulk pricing available at higher volumes. You pay only for records confirmed
              as deceased. Fees are due according to the terms presented at signup or in your agreement.
            </p>
            <p>
              Records we cannot confirm are not billed. If a submitted record returns no confirmed
              match, no charge is applied to it, and that behavior is not something we change without
              notice.
            </p>

            <h2 id="refunds">Refunds and billing disputes</h2>
            <p>
              We would rather fix a billing problem than argue about one. If you believe you have been
              charged incorrectly, contact
              <a [href]="'mailto:' + site.email">{{ site.email }}</a> within 60 days of the charge with
              your account details and the batch or transaction in question. We will investigate and
              respond with our findings.
            </p>
            <p>Where a charge is not valid, we credit or refund it. In practice that covers:</p>
            <ul>
              <li>
                Records billed as confirmed matches that were not, in fact, confirmed deceased matches
              </li>
              <li>Duplicate charges for the same record within the same batch</li>
              <li>Charges applied after you closed your account or revoked an API key</li>
              <li>Charges resulting from a platform error on our side</li>
            </ul>
            <p>
              Approved refunds are returned to the original payment method, or credited to your account
              balance if you prefer, normally within ten business days of the decision. Prepaid wallet
              balances that have not been consumed can be refunded on request when you close your
              account, less any usage already incurred.
            </p>
            <p>
              Because delivery is immediate and per record, we do not refund confirmed matches that were
              correctly returned and correctly billed simply because the result was not the one you
              expected. If you believe a specific match is wrong, raise it with us and we will review
              the underlying record. Where we agree the match was in error, that record is refunded.
            </p>
            <p>
              Please raise a concern with us before initiating a chargeback with your card issuer. A
              chargeback filed without contacting us first may lead to suspension of the account while
              the claim is resolved, and we will always try to settle the issue directly and faster than
              a card network can.
            </p>

            <h2 id="cancellation">Cancellation and account closure</h2>
            <p>
              There is no minimum term, no notice period and no cancellation fee on our standard pay per
              record pricing. You can stop using the service at any time, and you can close your account
              from your account settings or by emailing
              <a [href]="'mailto:' + site.supportEmail">{{ site.supportEmail }}</a>.
            </p>
            <p>
              On closure we stop billing immediately, revoke your API keys, and delete the records you
              submitted in line with our retention schedule. You are responsible for downloading any
              result files you still need before closure. Enterprise agreements with committed volumes
              follow the cancellation terms set out in that agreement, which take precedence over this
              section.
            </p>
            <p>
              We may suspend or close an account that is used outside the permitted purposes described
              above, that puts the platform or its data at risk, or that has fees outstanding after
              reasonable notice.
            </p>

            <h2 id="support">Support and availability</h2>
            <p>
              We aim to keep the platform and API available at all times, and we publish planned
              maintenance in advance where it will be noticeable. Support requests sent to
              <a [href]="'mailto:' + site.supportEmail">{{ site.supportEmail }}</a> receive a response
              on the next business day or sooner. Enterprise customers can agree specific availability
              and response commitments in a written service level agreement.
            </p>

            <h2 id="ip">Intellectual property</h2>
            <p>
              The service, including its software, content and data compilations, is owned by us and
              our licensors. These terms do not grant you any right to our trademarks or to reproduce
              the service beyond normal use.
            </p>

            <h2 id="liability">Limitation of liability</h2>
            <p>
              To the fullest extent permitted by law, we are not liable for indirect, incidental or
              consequential damages arising from your use of the service. Our total liability is
              limited to the amount you paid for the service in the period giving rise to the claim.
            </p>

            <h2 id="disputes">Dispute resolution and governing law</h2>
            <p>
              If something goes wrong, the fastest route to a resolution is a direct one. Contact
              <a [href]="'mailto:' + site.email">{{ site.email }}</a> with a written description of the
              issue and what you would like us to do about it. We will acknowledge it within five
              business days and work with you in good faith to resolve it. Most issues are settled at
              this stage.
            </p>
            <p>
              If a dispute is not resolved within 30 days of that first written notice, either party may
              escalate it. These terms and any dispute arising from them are governed by the laws of the
              State of California, without regard to its conflict of law rules, and the state and
              federal courts located in San Bernardino County, California have jurisdiction, unless your
              signed agreement with us specifies otherwise.
            </p>
            <p>
              Nothing in this section prevents either party from seeking urgent relief where it is
              needed to protect confidential information, intellectual property or the security of the
              platform.
            </p>

            <h2 id="changes">Changes to these terms</h2>
            <p>
              We may update these terms from time to time. When we do, we will update the date at the
              top of this page. Continued use of the service after changes take effect means you accept
              the updated terms.
            </p>

            <h2 id="contact">Contact us</h2>
            <p>
              Questions about these terms can be sent to
              <a [href]="'mailto:' + site.email">{{ site.email }}</a> or mailed to {{ site.address }}.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
</main>

<app-footer />
